import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Measure from "react-measure";

import {
	DataGrid,
	ProgressIndicator
} from "rhombus-react";

import { useTooltipRebuild } from "hooks/useTooltipRebuild";
import { VersionsSocketListener } from "hooks/useVersionsSocket";
import { useOnMountFTMessage } from "hooks/Notification";

import { CreativeVersions } from "reducers/CreativeVersions";
import { CREATIVE_SOCIAL_VERSION_COLUMNS, CREATIVE_VERSION_COLUMNS } from "widgets/grid/columns/Versions";
import { getPredictiveScoreColumn } from "widgets/grid/cells/PredictiveScore";

import { useGridScrollUtils } from "utils/DataGrid";
import {
	MIN_GRID_HEIGHT
	, DEFAULT_ROW_HEIGHT
	, JCRM_SOCKET
} from "utils/constants";
import { mappedSelectedVersions } from "selectors/Versions";
import CustomRowRenderer from "utils/columns/LockedRow";
import { getLibraryAutoClassificationEnabled, getManualClassificationEnabled } from "selectors/Intelligence";
import { usePredictiveScoringEnabled } from "hooks/usePredictiveScoringEnabled";
import { loadScores, loadLibraryKpis, PREDICTIVE_SCORE_ENTITY_TYPES } from "reducers/PredictiveScoring";
import { getPrimaryKpi } from "selectors/PredictiveScoring";
import { isPredictiveScoringTask, isFailedScoringTask } from "../../socket/formatters/predictiveScoringTask";
import { isScoringSupportedAdType } from "widgets/actions/PredictiveScoringAction";
import { isSocialCreative } from "utils/common";

function filterVisibleColumns(props = {}) {
	const { isLibraryAutoClassificationEnabled, isManualClassificationEnabled, isSocial, isPredictiveScoringOn, isScoringSupported, libraryId, creativeId } = props;

	let columns = isSocial ? CREATIVE_SOCIAL_VERSION_COLUMNS : CREATIVE_VERSION_COLUMNS;

	if (!isLibraryAutoClassificationEnabled || !isManualClassificationEnabled) {
		columns = columns.filter((column) => {
			if (column.key === "autoClassificationStatus") {
				return isLibraryAutoClassificationEnabled;
			} else if (column.key === "labelsCnt") {
				return isManualClassificationEnabled;
			}
			return true;
		});
	}

	if (isPredictiveScoringOn && isScoringSupported) {
		const labelsIndex = columns.findIndex(col => col.key === "labelsCnt");
		const insertIndex = labelsIndex >= 0 ? labelsIndex : columns.length;
		const predictiveScoreColumn = getPredictiveScoreColumn({ entityType: PREDICTIVE_SCORE_ENTITY_TYPES.VERSION, libraryId, creativeId });
		columns = [...columns.slice(0, insertIndex), predictiveScoreColumn, ...columns.slice(insertIndex)];
	}

	return columns;
}

export default function Grid({ sort="", libraryId, creativeId, isLoading }){
	const dispatch = useDispatch();
	const [sortColumn, sortDirection] = sort.split(":");

	const creativeVersions = useSelector(state => state.creativeVersions.items);
	const isLoaded = useSelector(state => state.creativeVersions.isLoaded);
	const adType = useSelector(state => state.creativeDetails.adType);
	const mappedVersions = useSelector(state => mappedSelectedVersions(state));
	const isLibraryAutoClassificationEnabled = useSelector(getLibraryAutoClassificationEnabled);
	const isManualClassificationEnabled = useSelector(getManualClassificationEnabled);
	const isAdvertiserLoaded = useSelector(state => state.advertiserDetails?.isLoaded !== false);
	const isPredictiveScoringOn = usePredictiveScoringEnabled();
	const primaryKpiId = useSelector(state => getPrimaryKpi(state)?.idScoringKpi ?? null);
	const isSocial = isSocialCreative({ adType });
	const isScoringSupported = isScoringSupportedAdType(adType);

	const lastLoadedRef = useRef(null);
	useEffect(() => {
		if (isPredictiveScoringOn && isScoringSupported && libraryId && creativeId) {
			const last = lastLoadedRef.current;
			const isSameEntityAsLast = !!last && last.libraryId === libraryId && last.creativeId === creativeId;
			const isKpiSwitch = isSameEntityAsLast
				&& last.primaryKpiId !== null && primaryKpiId !== null && last.primaryKpiId !== primaryKpiId;
			if (!isSameEntityAsLast || isKpiSwitch) {
				dispatch(loadScores(libraryId, PREDICTIVE_SCORE_ENTITY_TYPES.VERSION, { creativeId, skipCache: isKpiSwitch }));
			}
			const isNewEntity = !!last && !isSameEntityAsLast;
			lastLoadedRef.current = { libraryId, creativeId, primaryKpiId: isNewEntity ? null : primaryKpiId };
		}
	}, [dispatch, isPredictiveScoringOn, isScoringSupported, libraryId, creativeId, primaryKpiId]);

	const latestRef = useRef({ isPredictiveScoringOn, isScoringSupported, libraryId, creativeId });
	latestRef.current = { isPredictiveScoringOn, isScoringSupported, libraryId, creativeId };

	useEffect(() => {
		if (isPredictiveScoringOn && libraryId) {
			dispatch(loadLibraryKpis(libraryId));
		}
	}, [dispatch, isPredictiveScoringOn, libraryId]);

	useOnMountFTMessage((e) => {
		const message = e?.detail?.message || {}
			, { task, status, data } = message;

		if (message.origin !== JCRM_SOCKET) { return; }

		if (isFailedScoringTask(task) || (isPredictiveScoringTask(task) && /success/i.test(status))) {
			const { isPredictiveScoringOn: currentlyOn, isScoringSupported: currentlySupported, libraryId: currentLibraryId, creativeId: currentCreativeId } = latestRef.current;
			if (currentlyOn && currentlySupported && currentLibraryId && currentCreativeId) {
				dispatch(loadScores(currentLibraryId, PREDICTIVE_SCORE_ENTITY_TYPES.VERSION, { creativeId: currentCreativeId, skipCache: true }));
			}
			return;
		}

		const creativeIdFromMessage = data?.creatives?.[0]?.id;
		if (/Generate assets and versions/gi.test(task)
			&& /success/gi.test(status)
			&& creativeIdFromMessage === Number(creativeId)) {
			dispatch(CreativeVersions.loadCreativeVersions(libraryId, creativeId));
		}
	});

	const visibleColumns = useMemo(() => {
		const visibleProps = {
			isLibraryAutoClassificationEnabled,
			isManualClassificationEnabled,
			isSocial,
			isScoringSupported,
			isPredictiveScoringOn: isAdvertiserLoaded ? isPredictiveScoringOn : false,
			libraryId,
			creativeId,
		};

		return filterVisibleColumns(visibleProps);
	}, [isAdvertiserLoaded, isLibraryAutoClassificationEnabled, isManualClassificationEnabled, isSocial, isScoringSupported, isPredictiveScoringOn, libraryId, creativeId]);

	useEffect(() => {
		if (isSocial) {
			dispatch(CreativeVersions.updateColumnFilter("status", { displayName: "Published" }));
		} else {
			dispatch(CreativeVersions.updateColumnFilter("status", { displayName: "Status" }));
		}
	}, [isSocial]);

	const dataGridRef = useGridScrollUtils([creativeVersions]);

	function getCreativeVersionRow(rowID) {
		return{ ...mappedVersions[rowID] } ;
	}

	useTooltipRebuild(creativeVersions);

	const dataGridProps = {
		columns: visibleColumns,
		rowGetter:  getCreativeVersionRow,
		rowsCount:  creativeVersions.length,
		rowRenderer: CustomRowRenderer,
		rowHeight:  DEFAULT_ROW_HEIGHT,
		sortColumn,
		sortDirection,
		onGridSort: (col, dir) => dispatch(CreativeVersions.setSort(col, dir)),
		enableCellAutoFocus: false, /*
			This is a ReactDataGrid setting that defaults to `true`, which seems to make it fight
			with anything else trying to utilize regular `tabindex` control flow, like blade forms.
		*/
	};

	return (
		<VersionsSocketListener>
			<Measure>
				{(obj) => {
					let gridHeight = MIN_GRID_HEIGHT;
					if(obj.contentRect && obj.contentRect.entry && obj.contentRect.entry.height) {
						gridHeight = obj.contentRect.entry.height;
					}

					return (
						<div className="measure-component" ref={obj.measureRef}>
							<ProgressIndicator isLoading={!isLoaded} />
							<div ref={dataGridRef}>
								<DataGrid
									{...dataGridProps}
									minHeight={gridHeight}
								/>
							</div>
						</div>
					);
				}}
			</Measure>
		</VersionsSocketListener>
	);
}

export const PRIVATE = { filterVisibleColumns };

Grid.displayName = "VersionGrid";
